import { useTranslation } from 'react-i18next';
import type { ChannelStatsItem, NotificationStatsResponse } from '../types/notificationCenter';

interface ChannelStatusCardsProps {
  stats: NotificationStatsResponse | undefined;
  isLoading: boolean;
}

/** 채널별 통계 라벨 i18n 키 (§12.3 SMS/PUSH 확장) */
const CHANNEL_STATS_LABEL_KEY: Record<string, string> = {
  EMAIL: 'notificationCenter.stats.email',
  WEBHOOK: 'notificationCenter.stats.webhook',
  SMS: 'notificationCenter.stats.sms',
  PUSH: 'notificationCenter.stats.push',
};

/** 채널별 상태 표시 dot 색상 (design-tokens CSS var 만 사용) */
const CHANNEL_DOT_COLOR: Record<string, string> = {
  EMAIL: 'var(--color-info)',
  WEBHOOK: 'var(--color-brand-secondary, var(--color-brand-primary))',
  SMS: 'var(--color-warning)',
  PUSH: 'var(--color-success)',
};

/** 단일 채널 상태 카드 */
function ChannelCard({ item }: { item: ChannelStatsItem }) {
  const { t } = useTranslation();
  const channelLabel = t(CHANNEL_STATS_LABEL_KEY[item.channel] ?? 'notificationCenter.stats.email');

  return (
    <div
      style={{
        flex: '1 1 280px',
        minWidth: 240,
        background: 'var(--color-surface-background)',
        border: '1px solid var(--color-surface-border)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--spacing-4)',
      }}
      aria-label={`${channelLabel} 채널 통계`}
    >
      <div
        style={{
          fontSize: 'var(--font-size-sm)',
          fontWeight: 700,
          color: 'var(--color-text-primary)',
          marginBottom: 'var(--spacing-3)',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--spacing-2)',
        }}
      >
        <span
          style={{
            display: 'inline-block',
            width: 8,
            height: 8,
            borderRadius: '50%',
            background: CHANNEL_DOT_COLOR[item.channel] ?? 'var(--color-info)',
          }}
          aria-hidden="true"
        />
        {channelLabel}
      </div>

      <div style={{ display: 'flex', gap: 'var(--spacing-4)' }}>
        {/* 대기 */}
        <div style={{ flex: 1, textAlign: 'center' }}>
          <div
            style={{
              fontSize: 'var(--font-size-xl)',
              fontWeight: 700,
              color: 'var(--color-warning)',
              fontFamily: 'var(--font-family-mono)',
            }}
          >
            {item.pending}
          </div>
          <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>
            {t('notificationCenter.stats.pending')}
          </div>
        </div>

        {/* 성공 */}
        <div style={{ flex: 1, textAlign: 'center' }}>
          <div
            style={{
              fontSize: 'var(--font-size-xl)',
              fontWeight: 700,
              color: 'var(--color-success)',
              fontFamily: 'var(--font-family-mono)',
            }}
          >
            {item.sent}
          </div>
          <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>
            {t('notificationCenter.stats.sent')}
          </div>
        </div>

        {/* 실패 */}
        <div style={{ flex: 1, textAlign: 'center' }}>
          <div
            style={{
              fontSize: 'var(--font-size-xl)',
              fontWeight: 700,
              color: 'var(--color-danger)',
              fontFamily: 'var(--font-family-mono)',
            }}
          >
            {item.failed}
          </div>
          <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>
            {t('notificationCenter.stats.failed')}
          </div>
        </div>
      </div>
    </div>
  );
}

/** 채널별 통계 카드 스켈레톤 */
function ChannelCardSkeleton() {
  return (
    <div
      style={{
        flex: '1 1 280px',
        minWidth: 240,
        background: 'var(--color-surface-background)',
        border: '1px solid var(--color-surface-border)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--spacing-4)',
      }}
      aria-busy="true"
    >
      <div
        style={{
          height: 14,
          width: '40%',
          borderRadius: 'var(--radius-sm)',
          background: 'var(--color-surface-elevated)',
          marginBottom: 'var(--spacing-3)',
          animation: 'pulse 1.5s ease-in-out infinite',
        }}
      />
      <div style={{ display: 'flex', gap: 'var(--spacing-4)' }}>
        {[1, 2, 3].map((i) => (
          <div key={i} style={{ flex: 1, textAlign: 'center' }}>
            <div
              style={{
                height: 28,
                width: '60%',
                margin: '0 auto var(--spacing-1)',
                borderRadius: 'var(--radius-sm)',
                background: 'var(--color-surface-elevated)',
                animation: 'pulse 1.5s ease-in-out infinite',
              }}
            />
            <div
              style={{
                height: 10,
                width: '80%',
                margin: '0 auto',
                borderRadius: 'var(--radius-sm)',
                background: 'var(--color-surface-elevated)',
                animation: 'pulse 1.5s ease-in-out infinite',
              }}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * ChannelStatusCards — 채널별 PENDING/SENT/FAILED 집계 카드
 * (이메일/Slack·Teams/SMS/푸시 — §12.3 SMS·PUSH 확장, `stats.channels` 응답 그대로 렌더)
 */
export function ChannelStatusCards({ stats, isLoading }: ChannelStatusCardsProps) {
  if (isLoading) {
    return (
      <div
        style={{ display: 'flex', gap: 'var(--spacing-4)', flexWrap: 'wrap', marginBottom: 'var(--spacing-4)' }}
        aria-label="채널별 통계 불러오는 중"
      >
        <ChannelCardSkeleton />
        <ChannelCardSkeleton />
        <ChannelCardSkeleton />
        <ChannelCardSkeleton />
      </div>
    );
  }

  if (!stats || stats.channels.length === 0) return null;

  return (
    <div
      style={{ display: 'flex', gap: 'var(--spacing-4)', flexWrap: 'wrap', marginBottom: 'var(--spacing-4)' }}
      role="region"
      aria-label="채널별 알림 통계"
    >
      {stats.channels.map((item) => (
        <ChannelCard key={item.channel} item={item} />
      ))}
    </div>
  );
}
